import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAccount } from 'hooks/api';
import { useCurrentCompanyList } from 'hooks/api/useCompanies';
import { extractIdsFromUrl } from '../components/common/sidebar/desktop/helpers';

interface AccountBusinessContextType {
  accountId: string | undefined;
  businessId: string | undefined;
  isLoading: boolean;
  setBusinessId: (businessId: string) => void;
}

const AccountBusinessContext = createContext<AccountBusinessContextType>({
  accountId: undefined,
  businessId: undefined,
  isLoading: true,
  setBusinessId: () => {},
});

export const useAccountBusiness = () => useContext(AccountBusinessContext);

interface Props {
  children: React.ReactNode;
}

export const AccountBusinessProvider = ({ children }: Props) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [businessId, setBusinessId] = useState<string | undefined>(undefined);

  // Extract IDs from URL
  const { accountId: urlAccountId, businessId: urlBusinessId } =
    extractIdsFromUrl(location.pathname);

  // Get account data if we have an accountId
  const { account } = useAccount(urlAccountId);

  // Get businesses for the account owner
  const { companies: userBusinesses } = useCurrentCompanyList(
    account?.ownerUserId,
  );

  useEffect(() => {
    const initializeIds = async () => {
      setIsLoading(true);

      // If we have both IDs in the URL, use them
      if (urlAccountId && urlBusinessId) {
        setBusinessId(urlBusinessId);
        setIsLoading(false);
        return;
      }

      // If we only have accountId, try to find a business
      if (urlAccountId) {
        // If we have businesses for this account, use the first one
        if (userBusinesses && userBusinesses.length > 0) {
          setBusinessId(userBusinesses[0].id);
        }
      }

      setIsLoading(false);
    };

    initializeIds();
  }, [urlAccountId, urlBusinessId, userBusinesses]);

  const value = {
    accountId: urlAccountId,
    businessId,
    isLoading,
    setBusinessId,
  };

  return (
    <AccountBusinessContext.Provider value={value}>
      {children}
    </AccountBusinessContext.Provider>
  );
};
